import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { CheckCircle, Code, Database, Cog, Shield, Users, Zap, Clock, Award, MessageSquare, ChevronDown, Smartphone, Monitor, Cloud } from "lucide-react";
import { useState } from "react";
import SEO from "@/components/SEO";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

const FAQ = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
      <button
        className="flex justify-between items-center w-full text-left py-3 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-lg">{question}</span>
        <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="mt-2 text-muted-foreground">
          {answer}
        </div>
      )}
    </div>
  );
};

export default function SoftwareDevelopment() {
  const { openDrawer } = useQuoteDrawer();
  
  const features = [
    {
      title: "Custom Software Applications",
      description: "Tailored solutions built specifically for your business needs and requirements.",
      icon: <Code className="h-8 w-8" />
    },
    {
      title: "Enterprise Solutions",
      description: "Scalable enterprise-grade applications that grow with your business.",
      icon: <Cog className="h-8 w-8" />
    },
    {
      title: "API Development & Integration",
      description: "Seamless integration with third-party services and custom API development.",
      icon: <Database className="h-8 w-8" />
    },
    {
      title: "Performance Optimization",
      description: "High-performance applications optimized for speed and efficiency.",
      icon: <CheckCircle className="h-8 w-8" />
    }
  ];

  // SVG Icons for Technologies
const ReactIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 128 128">
    <g fill="#61DAFB"><circle cx="64" cy="64" r="11.4"></circle><path d="M64 103.2c-22 0-40.5-12.3-46.1-28.8h92.2c-5.6 16.5-24.1 28.8-46.1 28.8z"></path><path d="M17.9 53.6c5.6-16.5 24.1-28.8 46.1-28.8s40.5 12.3 46.1 28.8H17.9z"></path></g>
  </svg>
);

const NodeJsIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 128 128">
    <path fill="#83CD29" d="M112.2 45.4L96 42.5v42.7c0 6.9-2.1 11.3-6.5 13.3-2.1.9-4.7.5-8.1-1.4-3.4-1.9-7.6-5.1-12.6-9.7-3.1-2.8-5.7-5.1-7.7-6.8-2.6-2.2-4.4-3.8-5.5-4.8-7.7 8.1-11.8 12.2-16.2 16.6-3.2 3.2-6.1 4.9-10.4 5.8-4.2.9-7.9.6-11.1-.8-3.2-1.4-5.9-4.1-8.2-8.1-2.2-4-3.4-8.8-3.4-14.5V45.3l-16.2 2.8c-1.1.2-2.1-.3-2.9-1.4-.8-1.1-1.1-2.3-.9-3.6 1.9-10.9 5.6-20.3 11.1-28.1 5.5-7.8 12.8-13.8 21.8-18.1 9-4.3 19.1-6.4 30.2-6.4 11.1 0 21.2 2.1 30.2 6.4 9 4.3 16.3 10.3 21.8 18.1 5.5 7.8 9.2 17.2 11.1 28.1.2 1.3-.1 2.5-.9 3.6-.8 1.1-1.8 1.7-2.9 1.4zM96 45.3v42.7c0 3.5-.9 5.9-2.8 7.1-1.9 1.2-4.4.9-7.5-1.1-3.1-2-6.8-5.1-11.1-9.2-3.2-3.2-5.9-6-8.2-8.5-2.3-2.5-4-4.3-5.1-5.4-1.1-1.1-2.2-2.1-3.4-3.1-1.2-1-2.5-1.9-4-2.7-1.5-.8-3.2-1.2-5.1-1.2-1.9 0-3.6.4-5.1 1.2-1.5.8-2.8 1.7-4 2.7-1.2 1-2.3 2-3.4 3.1-1.1 1.1-2.8 2.9-5.1 5.4-2.3 2.5-5 5.3-8.2 8.5-4.3 4.1-8 7.2-11.1 9.2-3.1 2-5.6 2.3-7.5 1.1-1.9-1.2-2.8-3.6-2.8-7.1V45.3c0-3.5.9-5.9 2.8-7.1 1.9-1.2 4.4-.9 7.5 1.1 3.1 2 6.8 5.1 11.1 9.2 3.2 3.2 5.9 6 8.2 8.5 2.3 2.5 4 4.3 5.1 5.4 1.1 1.1 2.2 2.1 3.4 3.1 1.2 1 2.5 1.9 4 2.7 1.5.8 3.2 1.2 5.1 1.2 1.9 0 3.6-.4 5.1-1.2 1.5-.8 2.8-1.7 4-2.7 1.2-1 2.3-2 3.4-3.1 1.1-1.1 2.8-2.9 5.1-5.4 2.3-2.5 5-5.3 8.2-8.5 4.3-4.1 8-7.2 11.1-9.2 3.1-2 5.6-2.3 7.5-1.1 1.9 1.2 2.8 3.6 2.8 7.1z"></path>
  </svg>
);

const PythonIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 128 128">
    <path fill="#FFD43B" d="M63.391 1.988c-4.222.02-8.252.379-11.8.971l-3.549.223c-6.922.588-11.537 1.28-14.71 2.92-2.4 1.24-4.07 2.85-5.07 4.95-1.23 2.41-1.84 5.32-1.84 8.75v10.5h31.9v3.5h-45.98c-7.94 0-12.15 3.66-14.1 7.59-1.88 3.84-2.02 9.6-2.02 15.12v11.34c0 6.16.2 12.6 2.9 17.06 2.56 4.24 7.21 7.02 14.91 7.12h10.79v-9.5c0-8.77 7.67-16.02 16.47-16.02h31.77c7.33 0 11.76-3.48 11.76-10.96v-30.9c0-5.3-1.72-8.55-5.91-10.15-2.15-.82-4.64-1.31-7.28-1.4-1.82-.05-3.87-.08-6.13-.08l-3.14.01zm-21.29 7.01c2.59 0 4.7 2.11 4.7 4.7 0 2.6-2.11 4.7-4.7 4.7s-4.7-2.1-4.7-4.7c0-2.59 2.11-4.7 4.7-4.7z"></path>
    <path fill="#FFD43B" d="M123.4 63.338c0-6.16-.2-12.6-2.9-17.06-2.56-4.24-7.21-7.02-14.91-7.12h-10.79v9.5c0 8.77-7.67 16.02-16.47 16.02h-31.77c-7.33 0-11.76 3.48-11.76 10.96v30.9c0 5.3 1.72 8.55 5.91 10.15 2.15.82 4.64 1.31 7.28 1.4 1.82.05 3.87.08 6.13.08l3.14-.01c4.22-.02 8.25-.38 11.8-.97l3.55-.22c6.92-.59 11.54-1.28 14.71-2.92 2.4-1.24 4.07-2.85 5.07-4.95 1.23-2.41 1.84-5.32 1.84-8.75v-10.5h-31.9v-3.5h45.98c7.94 0 12.15-3.66 14.1-7.59 1.88-3.84 2.02-9.6 2.02-15.12v-11.34z"></path>
    <path fill="#306998" d="M85.5 125.928c-2.59 0-4.7-2.11-4.7-4.7 0-2.6 2.11-4.7 4.7-4.7s4.7 2.1 4.7 4.7c0 2.59-2.11 4.7-4.7 4.7z"></path>
  </svg>
);

const JavaIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24">
    <path fill="#007396" d="M9.37 17.4c-3.97 0-5.8-1.65-5.8-3.2 0-1.54 1.83-2.6 4.3-3.1-1.1-.5-1.85-1-2.33-1.5-.48-.5-.72-1-.72-1.5 0-.5.12-1 .36-1.4-2.17 1.3-3.37 3.1-3.37 5.1 0 3.1 2.7 5.3 7.56 5.3 1.2 0 2.3-.1 3.3-.3-.5-.5-1-1-1.5-1.5-.9.2-1.9.2-2.8.2zm-3.2-9.6c0 .6.3 1.2.9 1.7.6.5 1.5 1 2.6 1.4 1.1.4 2.4.6 3.8.6.8 0 1.6-.1 2.4-.3.8-.2 1.5-.4 2.1-.7.6-.3 1.1-.6 1.5-.9.4-.3.6-.6.6-.9 0-.3-.2-.6-.6-.9-.4-.3-1-.6-1.7-.9-.7-.3-1.6-.5-2.6-.7-1-.2-2.1-.3-3.3-.3-1.2 0-2.3.1-3.3.3-1 .2-1.9.5-2.6.8-.7.3-1.3.6-1.7 1-.4.4-.6.7-.6 1.1z"></path>
    <path fill="#007396" d="M15.17 18.6c1.1.8 2.6 1.5 4.2 1.5 1.7 0 2.5-.7 2.5-1.5 0-1-1.2-1.4-3.5-2.1-2.3-.7-3.8-1.7-3.8-3.5 0-1.8 1.5-3.2 4.2-3.2.9 0 1.8.1 2.6.4.8.3 1.5.7 2.1 1.3l-1.2 1.2c-.4-.4-.9-.8-1.5-1-.6-.3-1.3-.4-2-.4-1.6 0-2.4.7-2.4 1.4 0 .9 1.2 1.3 3.5 2 2.3.7 3.8 1.7 3.8 3.6 0 1.9-1.6 3.4-4.5 3.4-1 0-2-.2-3-.5-1-.3-1.9-.8-2.7-1.5l1.3-1.2z"></path>
    <path fill="#000" d="M12 24c6.6 0 12-5.4 12-12S18.6 0 12 0 0 5.4 0 12s5.4 12 12 12zm0-22c5.5 0 10 4.5 10 10s-4.5 10-10 10S2 17.5 2 12 6.5 2 12 2z"></path>
  </svg>
);

const CSharpIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 128 128">
    <path fill="#9B4F96" d="M115.4 30.7l-48.3-27.8c-.8-.5-1.9-.7-3.1-.7-1.2 0-2.3.3-3.1.7l-48 27.9c-1.7 1-2.9 3.5-2.9 5.4v55.7c0 1.1.2 2.4 1 3.5l106.8-62c-.6-1.2-1.3-2.1-2.2-2.8z"></path>
    <path fill="#68217A" d="M10.7 95.3c.5.8 1.2 1.5 1.9 1.9l48.2 27.9c.8.5 1.9.7 3.1.7 1.2 0 2.3-.3 3.1-.7l48-27.9c1.7-1 2.9-3.5 2.9-5.4v-55.7c0-.9-.1-1.9-.6-2.8l-106.6 62z"></path>
    <path fill="#fff" d="M85.3 76.1c-4.2 7.4-12.2 12.4-21.3 12.4-13.5 0-24.5-11-24.5-24.5s11-24.5 24.5-24.5c9.1 0 17.1 5 21.3 12.5l13-7.5c-6.8-11.9-19.6-20-34.3-20-21.8 0-39.5 17.7-39.5 39.5s17.7 39.5 39.5 39.5c14.6 0 27.4-8 34.2-19.8l-12.9-7.6z"></path>
    <path fill="#fff" d="M82.1 61.8h5.2v-5.3h4.4v5.3h5.3v4.4h-5.3v5.2h-4.4v-5.2h-5.2v-4.4zM100.6 61.8h5.2v-5.3h4.4v5.3h5.3v4.4h-5.3v5.2h-4.4v-5.2h-5.2v-4.4z"></path>
  </svg>
);

const DotNetIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 128 128">
    <path fill="#512BD4" d="M64 0c35.3 0 64 28.7 64 64s-28.7 64-64 64S0 99.3 0 64 28.7 0 64 0z"></path>
    <path fill="#fff" d="M64 12.8c-28.3 0-51.2 22.9-51.2 51.2S35.7 115.2 64 115.2 115.2 92.3 115.2 64 92.3 12.8 64 12.8zm-6.4 76.8H44.8V38.4h12.8v51.2zm25.6 0H70.4V38.4h12.8v51.2z"></path>
  </svg>
);

const PostgresIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 128 128">
    <path fill="#336791" d="M64 0C28.7 0 0 14.3 0 32v64c0 17.7 28.7 32 64 32s64-14.3 64-32V32C128 14.3 99.3 0 64 0zm0 16c26.5 0 48 9.6 48 16s-21.5 16-48 16S16 45.6 16 40s21.5-16 48-16zm0 88c-26.5 0-48-9.6-48-16v-16c0 6.4 21.5 16 48 16s48-9.6 48-16v16c0 6.4-21.5 16-48 16z"></path>
    <path fill="#336791" d="M64 48c-26.5 0-48-9.6-48-16s21.5-16 48-16 48 9.6 48 16-21.5 16-48 16z"></path>
  </svg>
);

const MongoIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24">
    <path fill="#13AA52" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.5v-7.5H8l4-8v7.5h3l-4 8z"></path>
    <path fill="#13AA52" d="M13 12v7.5l4-8h-3V4l-4 8h3z"></path>
  </svg>
);

const technologies = [
  { name: "React", icon: <ReactIcon /> },
  { name: "Node.js", icon: <NodeJsIcon /> },
  { name: "Python", icon: <PythonIcon /> },
  { name: "Java", icon: <JavaIcon /> },
  { name: "C#", icon: <CSharpIcon /> },
  { name: ".NET", icon: <DotNetIcon /> },
  { name: "PostgreSQL", icon: <PostgresIcon /> },
  { name: "MongoDB", icon: <MongoIcon /> }
];

  const process = [
    {
      step: "01",
      title: "Discovery & Planning",
      description: "We analyze your requirements and create a detailed project roadmap"
    },
    {
      step: "02",
      title: "UI/UX Design",
      description: "Crafting intuitive user experiences and beautiful interfaces"
    },
    {
      step: "03",
      title: "Development",
      description: "Agile development with continuous integration and testing"
    },
    {
      step: "04",
      title: "Deployment & Support",
      description: "Smooth deployment with 24/7 monitoring and maintenance"
    }
  ];

  const whyChooseUs = [
    {
      icon: <Award className="h-8 w-8" />,
      title: "Proven Expertise",
      description: "10+ years of delivering successful software solutions"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Secure & Scalable",
      description: "Enterprise-grade security and scalability built-in"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Dedicated Team",
      description: "Experienced developers committed to your success"
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Fast Delivery",
      description: "Rapid development without compromising on quality"
    }
  ];

  const testimonials = [
    {
      quote: "Kecly transformed our business operations with their custom ERP solution. Highly recommended!",
      author: "Ahmed Al Maktoum",
      role: "CEO, TechGulf Solutions"
    },
    {
      quote: "The team delivered our project on time and exceeded our expectations. Will work with them again.",
      author: "Sarah Johnson",
      role: "Product Manager, InnovateX"
    }
  ];

  const faqs = [
    {
      question: "How long does custom software development take?",
      answer: "The timeline varies based on project complexity, but most projects take between 3-6 months from start to deployment."
    },
    {
      question: "What technologies do you use for development?",
      answer: "We use modern tech stacks including React, Node.js, Python, .NET, and more, choosing the best tools for your specific needs."
    },
    {
      question: "Do you provide post-launch support?",
      answer: "Yes, we offer comprehensive maintenance and support packages to ensure your software continues to perform optimally."
    }
  ];

  return (
    <>
      <SEO 
        title="Custom Software Development UAE | Kecly"
        description="Professional custom software solutions in UAE. Tailored business applications built for efficiency and growth since 2017."
        keywords="software development uae, custom business apps dubai, enterprise solutions sharjah"
      />

      {/* Hero Section */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 relative overflow-hidden bg-white text-gray-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/grid.svg')] [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
                <Code className="w-4 h-4 mr-2" />
                Custom Software Solutions
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
                Transform Your <span className="text-blue-600">Business</span> with Custom Software
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0">
                We build scalable, secure, and high-performance software solutions tailored to your unique business needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button onClick={openDrawer} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-semibold">
                  Get Free Quote
                </Button>
                <Link href="/portfolio">
                  <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-6 text-base font-semibold">
                    View Our Work
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap justify-center lg:justify-start gap-6">
                {["Web Apps", "Mobile Apps", "Cloud Solutions", "Enterprise Software"].map((item) => (
                  <div key={item} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Code Editor Mockup */}
            <motion.div
              initial={{ opacity: 0, y: 50, rotate: -2 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative w-full max-w-2xl mx-auto lg:mx-0"
            >
              <div className="relative z-10 rounded-lg overflow-hidden shadow-2xl border border-gray-200 bg-white">
                {/* Editor Header */}
                <div className="bg-gray-100 px-4 py-2 flex items-center justify-between border-b border-gray-200">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">index.tsx</div>
                  <div className="w-8"></div>
                </div>
                
                {/* Sidebar */}
                <div className="flex h-96">
                  <div className="w-12 bg-gray-100 border-r border-gray-200 flex flex-col items-center py-4 space-y-4">
                    {['files', 'search', 'git', 'debug', 'extensions'].map((icon, i) => (
                      <div key={i} className={`p-2 rounded ${i === 0 ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-300'}`}>
                        <div className="w-5 h-5"></div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Code Area */}
                  <div className="flex-1 overflow-auto p-4 font-mono text-sm">
                    <div className="text-gray-500 select-none">
                      <div className="mb-4">
                        <span className="text-purple-400">import</span> React <span className="text-purple-400">from</span> <span className="text-yellow-300">'react'</span>;
                      </div>
                      <div className="mb-4">
                        <span className="text-purple-400">import</span> <span className="text-blue-400">
                          {'{ Button, Card, Container }'}
                        </span> <span className="text-purple-400">from</span> <span className="text-yellow-300">'@/components'</span>;
                      </div>
                      <div className="mb-4">
                        <span className="text-purple-400">const</span> <span className="text-blue-400">App</span> = () <span className="text-purple-400">=&gt;</span> <span className="text-gray-400">
                          {'{ /* Component body */ }'}
                        </span>
                      </div>
                      <div className="ml-4 mb-4">
                        <span className="text-purple-400">return</span> (
                          <span className="text-gray-400">{'<'}</span>
                          <span className="text-blue-400">Container</span>
                          <span className="text-gray-400">{'>'}</span>
                        );
                      </div>
                      <div className="ml-8 mb-4">
                        <span className="text-gray-400">{'<'}</span>
                        <span className="text-blue-400">h1</span>
                        <span className="text-green-400"> className=</span>
                        <span className="text-yellow-300">"text-3xl font-bold mb-6"</span>
                        <span className="text-gray-400">{'>'}</span>
                        <span className="text-gray-300">Welcome to Our App</span>
                        <span className="text-gray-400">{'</'}</span>
                        <span className="text-blue-400">h1</span>
                        <span className="text-gray-400">{'>'}</span>
                      </div>
                      <div className="ml-8 mb-4">
                        <span className="text-gray-400">{'<'}</span>
                        <span className="text-blue-400">Button</span>
                        <span className="text-green-400"> variant=</span>
                        <span className="text-yellow-300">"primary"</span>
                        <span className="text-gray-400">{'>'}</span>
                        <span className="text-gray-300">Get Started</span>
                        <span className="text-gray-400">{'</'}</span>
                        <span className="text-blue-400">Button</span>
                        <span className="text-gray-400">{'>'}</span>
                      </div>
                      <div className="ml-4">
                        <span className="text-gray-400">);</span>
                      </div>
                      <div>
                        <span className="text-gray-400">{'}'}</span>
                      </div>
                      <div className="mt-4">
                        <span className="text-purple-400">export default</span> <span className="text-blue-400">App</span>;
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Terminal */}
                <div className="bg-gray-100 border-t border-gray-200 p-3">
                  <div className="flex items-center text-xs text-gray-500 mb-2">
                    <span className="mr-4">TERMINAL</span>
                    <span className="text-green-400">$</span> npm start
                  </div>
                  <div className="text-xs text-green-400 font-mono">
                    <div>Compiled successfully!</div>
                    <div>App running at http://localhost:3000</div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-blue-500/10 rounded-full filter blur-3xl -z-10"></div>
              <div className="absolute -top-4 -left-4 w-40 h-40 bg-purple-500/10 rounded-full filter blur-3xl -z-10"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[url('/grid.svg')] [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
        </div>
        <div className="absolute -right-32 -top-32 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute -left-32 -bottom-32 w-80 h-80 bg-purple-500/10 rounded-full filter blur-3xl"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">
              Our Services
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive <span className="text-blue-600">Software Solutions</span>
            </h2>
            <p className="text-lg text-gray-600">
              We deliver end-to-end software development services that drive digital transformation and business growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="group bg-gray-100 p-6 rounded-xl border border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-700 mb-5 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
                <div className="mt-4 flex items-center text-sm font-medium text-blue-700 group-hover:text-blue-500 transition-colors">
                  Learn more
                  <svg className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Button 
              onClick={openDrawer} 
              className="px-8 py-6 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Start Your Project Today
            </Button>
            <p className="mt-4 text-sm text-gray-600">
              Have questions?{' '}
              <Link href="/contact" className="text-blue-600 hover:underline font-medium">
                Talk to our experts
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 md:py-24 bg-white text-gray-900 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[url('/grid.svg')] [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">
              Our Tech Stack
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Technologies We <span className="text-blue-600">Excel In</span>
            </h2>
            <p className="text-lg text-gray-600">
              We leverage cutting-edge technologies to build robust, scalable, and high-performance applications.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <div className="flex flex-col items-center p-4 bg-gray-100 rounded-xl border border-gray-200 hover:border-blue-400 transition-all duration-300 h-full">
                  <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center text-3xl text-gray-700 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300 mb-3">
                    {tech.icon}
                  </div>
                  <span className="text-sm font-medium text-gray-700 text-center">{tech.name}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-600 mb-6">
              Don't see your preferred technology? We're always expanding our expertise!
            </p>
            <Button 
              variant="outline" 
              onClick={openDrawer}
              className="px-6 py-5 text-base font-medium border-2 border-gray-300 hover:border-blue-400 hover:bg-transparent text-gray-700 hover:text-blue-500 transition-colors"
            >
              Discuss Your Tech Stack
            </Button>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Our Development Process</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Proven methodology that ensures successful project delivery
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="font-orbitron font-bold text-blue-700">{item.step}</span>
                </div>
                <h3 className="font-orbitron font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Why Choose Kecly?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We deliver exceptional value through innovative solutions and dedicated service
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-8 rounded-xl text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 text-blue-700">
                  {item.icon}
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">What Our Clients Say</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="glass-effect p-8 rounded-xl"
              >
                <div className="flex items-center mb-6">
                  <div className="text-yellow-400 mr-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i}>★</span>
                    ))}
                  </div>
                </div>
                <p className="text-lg italic mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold mr-4">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold">{testimonial.author}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <RelatedServices 
            currentService="Software Development" 
            services={[
              {
                title: "Mobile App Development",
                description: "Native and cross-platform mobile applications for iOS and Android.",
                icon: <Smartphone className="h-8 w-8" />
              },
              {
                title: "Web Design & Development",
                description: "Custom web applications and responsive websites for your business.",
                icon: <Monitor className="h-8 w-8" />
              },
              {
                title: "Cloud Services",
                description: "Cloud-based solutions for scalability, reliability and performance.",
                icon: <Cloud className="h-8 w-8" />
              }
            ]} 
          />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Find answers to common questions about our software development services
            </p>
          </motion.div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <FAQ key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      <WebDesignLocations />

      {/* CTA Section */}
      <section className="py-20 bg-blue-100">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-orbitron font-bold text-4xl md:text-5xl text-blue-600 mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Let's build something amazing together. Get a free consultation and project estimate today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openDrawer} 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold"
                size="lg"
              >
                Start Your Project Now
                <Zap className="ml-2 h-5 w-5" />
              </Button>
              <Link href="/contact">
                <Button 
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-8 py-6 text-lg font-semibold"
                  size="lg"
                >
                  Schedule a Call
                  <MessageSquare className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-sm text-gray-600">
              No obligation. No commitment. Just a friendly chat about your project.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
